import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { assetUrl } from '../../utils/assets';

// Punchy 4-word cycle. Every phrase lives on its own — no dependent clause,
// no vendor talk. Reads as a rolling promise as the type morphs.
const PHRASES = [
  'YOUR VOICE.',
  '32 LANGUAGES.',
  'LIP-SYNCED.',
  'DONE FOR YOU.',
];

const PHRASE_MS = 3200;

// Background reel. 1080p H.264, AAC stereo audio, faststart-encoded so
// it starts playing before the full file is downloaded. Starts muted
// (browser autoplay policy) — user opts in via the corner button.
const BG_VIDEO = assetUrl('multipleLang.mp4', '/videos/multipleLang.mp4');

// Character-level enter/exit — staggered per-letter, gives the morph
// a "settling" feel instead of a hard crossfade.
const charVariants = {
  hidden: { opacity: 0, y: 24, filter: 'blur(10px)' },
  visible: (i) => ({
    opacity: 1, y: 0, filter: 'blur(0px)',
    transition: { duration: 0.45, delay: i * 0.022, ease: [0.16, 1, 0.3, 1] },
  }),
  exit: (i) => ({
    opacity: 0, y: -24, filter: 'blur(10px)',
    transition: { duration: 0.3, delay: i * 0.012, ease: [0.4, 0, 1, 1] },
  }),
};

export default function HeroWaveform() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [mountDone, setMountDone] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    // If unmuting for the first time, browser may pause — nudge play back.
    if (!v.muted && v.paused) v.play().catch(() => {});
    setMuted(v.muted);
  };

  useEffect(() => {
    const t = setTimeout(() => setMountDone(true), 700);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % PHRASES.length);
    }, PHRASE_MS);
    return () => clearInterval(id);
  }, []);

  const currentPhrase = PHRASES[idx];

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100vh',
        background: '#000',
        overflow: 'hidden',
      }}
    >
      {/* Background reel — starts muted so browser allows autoplay.
          User can unmute via the button bottom-right. */}
      <Box
        component="video"
        ref={videoRef}
        src={BG_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Readability overlay: solid dim + edge vignette. Keeps the reel
          visible but pushes contrast up so the type reads cleanly. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 1,
          background: `
            linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.55) 100%),
            radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.55) 100%)
          `,
        }}
      />

      {/* Content stack */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ marginBottom: 32 }}
        >
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.5, borderRadius: '100px',
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(8px)',
          }}>
            <Box
              component={motion.div}
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              sx={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', flexShrink: 0 }}
            />
            <Typography sx={{
              color: 'rgba(255,255,255,0.55)', fontSize: '10px', fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase', fontFamily: 'Inter',
            }}>
              AI Video Dubbing · 32 Languages
            </Typography>
          </Box>
        </motion.div>

        {/* Kinetic headline — cycles through PHRASES */}
        <Box
          sx={{
            textAlign: 'center',
            minHeight: 'clamp(3rem, 8vw, 9rem)',
            perspective: '1000px',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.h1
              key={idx}
              style={{
                margin: 0,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 900,
                fontSize: 'clamp(2.4rem, 7vw, 7.5rem)',
                letterSpacing: '-0.04em',
                color: '#fff',
                lineHeight: 1.02,
                display: 'inline-block',
                whiteSpace: 'nowrap',
                textShadow: '0 0 80px rgba(0,0,0,0.6)',
              }}
            >
              {currentPhrase.split('').map((c, i) => (
                <motion.span
                  key={`${idx}-${i}`}
                  custom={i}
                  variants={charVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ display: 'inline-block', whiteSpace: 'pre' }}
                >
                  {c === ' ' ? ' ' : c}
                </motion.span>
              ))}
            </motion.h1>
          </AnimatePresence>
        </Box>

        {/* Phrase progress dots — subtle, tells you the cycle is finite */}
        <Box sx={{ mt: 4, display: 'flex', gap: 1.2 }}>
          {PHRASES.map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === idx ? 20 : 6,
                height: 2,
                borderRadius: 2,
                background: i === idx ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.18)',
                transition: 'width 0.5s ease, background 0.5s ease',
              }}
            />
          ))}
        </Box>

        {/* Tagline + CTAs */}
        <AnimatePresence>
          {mountDone && (
            <motion.div
              key="cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              style={{ textAlign: 'center', marginTop: 40 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 300,
                  fontSize: 'clamp(0.9rem, 1.8vw, 1.1rem)',
                  color: 'rgba(255,255,255,0.55)',
                  letterSpacing: '0.02em',
                  mb: 3.5,
                }}
              >
                AI dubbing that sounds like you — in every language.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    background: '#fff',
                    color: '#000',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    px: 3.5,
                    py: 1.4,
                    borderRadius: '2px',
                    '&:hover': { background: 'rgba(255,255,255,0.88)' },
                  }}
                >
                  Get in Touch
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/demo')}
                  sx={{
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: '#fff',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    px: 3.5,
                    py: 1.4,
                    borderRadius: '2px',
                    backdropFilter: 'blur(8px)',
                    background: 'rgba(255,255,255,0.04)',
                    '&:hover': {
                      border: '1px solid rgba(255,255,255,0.55)',
                      background: 'rgba(255,255,255,0.08)',
                    },
                  }}
                >
                  Watch Demo
                </Button>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Mute / unmute toggle — bottom-right */}
      <IconButton
        onClick={toggleMute}
        aria-label={muted ? 'Unmute background reel' : 'Mute background reel'}
        sx={{
          position: 'absolute',
          bottom: { xs: 20, md: 28 },
          right: { xs: 16, md: 28 },
          width: { xs: 40, md: 46 },
          height: { xs: 40, md: 46 },
          background: 'rgba(0,0,0,0.55)',
          color: '#fff',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.18)',
          zIndex: 4,
          '&:hover': { background: 'rgba(0,0,0,0.75)', borderColor: 'rgba(255,255,255,0.35)' },
        }}
      >
        {muted
          ? <VolumeOffIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
          : <VolumeUpIcon sx={{ fontSize: { xs: 18, md: 22 } }} />}
      </IconButton>

      {/* Scroll cue */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 28,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          zIndex: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: '0.62rem',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)',
          }}
        >
          Scroll
        </Typography>
        <motion.div
          animate={{ scaleY: [1, 1.5, 1], opacity: [0.3, 0.7, 0.3] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            width: 1,
            height: 32,
            background: 'rgba(255,255,255,0.4)',
            transformOrigin: 'top',
          }}
        />
      </Box>
    </Box>
  );
}
